import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft, Mail, Phone, Calendar, Cake, Shield, Edit2, Save, X, DollarSign,
  FileText, Upload, Download, Trash2, Award, TrendingUp, Star, Clock, Briefcase,
  GraduationCap, CheckCircle2, AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ScheduleTab } from "./StaffPage";

const roleColors = {
  stylist: "#8B5CF6", esthetician: "#F59E0B", nail_tech: "#EF4444",
  colorist: "#EC4899", support_staff: "#6B7280", front_desk: "#6B7280", owner: "#1A1A1A", manager: "#3B82F6",
};
const empTypeLabel = { "full-time": "Full-Time", "part-time": "Part-Time", "contract": "Contract" };
const DOC_CATEGORIES = [
  { id: "contract", label: "Contract" }, { id: "certification", label: "Certification" },
  { id: "id", label: "ID / License" }, { id: "tax", label: "Tax Form" },
  { id: "review", label: "Review" }, { id: "other", label: "Other" },
];

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return d; }
};
const fmtBytes = (b) => !b ? "" : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`;

const TABS = [
  { id: "overview", label: "Overview", icon: Briefcase },
  { id: "schedule", label: "Schedule", icon: Calendar },
  { id: "services", label: "Services", icon: Star },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "performance", label: "Performance", icon: TrendingUp },
];

function Stat({ label, value, accent = "slate" }) {
  const tones = {
    slate: "bg-[#FAFAFA] border-slate-200 text-slate-900",
    indigo: "bg-indigo-50 border-indigo-100 text-indigo-700",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
    rose: "bg-rose-50 border-rose-100 text-rose-700",
    amber: "bg-amber-50 border-amber-100 text-amber-700",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[accent]}`}>
      <p className="text-[10px] font-bold uppercase tracking-widest opacity-70 font-['Manrope']">{label}</p>
      <p className="font-['Outfit'] text-2xl font-semibold mt-1 tabular-nums">{value}</p>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="py-2.5 border-b border-slate-100">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 font-['Manrope']">{label}</p>
      <p className="text-sm text-slate-900 mt-0.5">{value || <span className="text-slate-300">—</span>}</p>
    </div>
  );
}

export default function StaffMemberPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const isManager = currentUser?.role === "owner" || currentUser?.role === "manager";
  const isSelf = currentUser?.user_id === userId;
  const canSeePay = isManager;
  const canSeeDocs = isManager;

  const loadProfile = useCallback(async () => {
    try {
      const [p, l] = await Promise.all([api.get(`/staff/${userId}/profile`), api.get("/locations")]);
      setProfile(p.data); setLocations(l.data || []);
    } catch { toast.error("Couldn't load employee"); }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const startEdit = () => {
    setForm({
      name: profile.name || "", title: profile.title || "", email: profile.email || "", phone: profile.phone || "",
      birthday: profile.birthday || "", start_date: profile.start_date || "", role: profile.role || "stylist",
      employment_type: profile.employment_type || "full-time", bio: profile.bio || "",
      specializations: (profile.specializations || []).join(", "),
      emergency_contact: profile.emergency_contact || "", emergency_phone: profile.emergency_phone || "",
      color: profile.color || "#B5A493",
      pay_type: profile.pay_type || (profile.commission_rate > 0 ? "commission" : "hourly"),
      commission_rate: profile.commission_rate || 0, hourly_rate: profile.hourly_rate || 0,
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        specializations: form.specializations ? form.specializations.split(",").map(s => s.trim()).filter(Boolean) : [],
        commission_rate: form.pay_type === "hourly" ? 0 : parseFloat(form.commission_rate) || 0,
        hourly_rate: parseFloat(form.hourly_rate) || 0,
      };
      await api.put(`/staff/${userId}`, payload);
      toast.success("Profile updated");
      setEditing(false);
      await loadProfile();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!profile) return null;

  if (!isManager && !isSelf) {
    return (
      <div className="p-8 max-w-xl mx-auto text-center" data-testid="staff-access-denied">
        <Shield className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <h2 className="font-['Outfit'] text-xl font-semibold text-slate-900">No access</h2>
        <p className="text-sm text-slate-500 mt-1">You don't have permission to view this employee's full profile.</p>
        <Button onClick={() => navigate("/staff")} className="mt-4 rounded-full"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Team</Button>
      </div>
    );
  }

  const s = profile.stats || {};
  const visibleTabs = TABS.filter(t => t.id !== "documents" || canSeeDocs);

  return (
    <div className="p-6 lg:p-8 max-w-[1100px] mx-auto" data-testid="staff-member-page">
      <button onClick={() => navigate("/staff")} data-testid="back-to-team" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-5 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Team
      </button>

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-5" style={{ background: `linear-gradient(135deg, ${profile.color || '#1A1A1A'}10, white 60%)` }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-semibold flex-shrink-0" style={{ backgroundColor: profile.color || '#1A1A1A' }}>
              {profile.name?.[0]}
            </div>
            <div>
              <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900">{profile.name}</h1>
              <p className="text-sm text-slate-600">{profile.title || profile.role?.replace('_', ' ')}</p>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <span className="text-xs px-2 py-0.5 rounded-full capitalize font-medium" style={{ backgroundColor: `${roleColors[profile.role] || '#B5A493'}15`, color: roleColors[profile.role] || '#B5A493' }}>{profile.role?.replace('_', ' ')}</span>
                {profile.employment_type && <span className="text-xs text-slate-400">{empTypeLabel[profile.employment_type]}</span>}
                {profile.clocked_in && <span className="text-xs bg-[#D4EDDA] text-[#155724] px-2 py-0.5 rounded-full">Clocked In</span>}
                {profile.is_active === false && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-100">Deactivated</span>}
              </div>
            </div>
          </div>
          {isManager && tab === "overview" && !editing && (
            <Button variant="outline" onClick={startEdit} data-testid="edit-overview-btn" className="rounded-full"><Edit2 className="w-3.5 h-3.5 mr-1.5" /> Edit</Button>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          <Stat label="Tenure" value={s.tenure_years ? `${s.tenure_years}y` : "—"} accent="indigo" />
          <Stat label="Completed" value={s.total_completed ?? 0} accent="emerald" />
          {canSeePay ? <Stat label="Month Rev" value={`$${s.month_revenue ?? 0}`} accent="amber" /> : <Stat label="This Month" value={s.month_appointments ?? 0} />}
          <Stat label="No-Shows" value={s.total_noshows ?? 0} accent="rose" />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-5 overflow-x-auto">
        {visibleTabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${tab === t.id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-700"}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && (editing ? (
        <OverviewEdit form={form} setForm={setForm} save={save} saving={saving} cancel={() => setEditing(false)} canSeePay={canSeePay} isOwner={currentUser?.role === "owner"} />
      ) : (
        <div className="grid md:grid-cols-2 gap-x-8" data-testid="overview-view">
          <div>
            <Field label="Email" value={profile.email} />
            <Field label="Phone" value={profile.phone} />
            <Field label="Start Date" value={fmtDate(profile.start_date)} />
            <Field label="Birthday" value={fmtDate(profile.birthday)} />
            <Field label="Employment" value={empTypeLabel[profile.employment_type]} />
          </div>
          <div>
            <Field label="Emergency Contact" value={profile.emergency_contact} />
            <Field label="Emergency Phone" value={profile.emergency_phone} />
            {canSeePay && <Field label="Pay" value={profile.pay_type === "hourly" ? `$${profile.hourly_rate}/hr` : `${Math.round((profile.commission_rate || 0) * 100)}% commission`} />}
            <Field label="Specializations" value={(profile.specializations || []).join(", ")} />
            <Field label="Bio" value={profile.bio} />
          </div>
        </div>
      ))}

      {tab === "schedule" && <ScheduleTab userId={userId} locations={locations} />}
      {tab === "services" && <ServicesTab userId={userId} isManager={isManager} />}
      {tab === "documents" && canSeeDocs && <DocumentsTab userId={userId} />}
      {tab === "performance" && <PerformanceTab userId={userId} stats={s} canSeePay={canSeePay} />}
    </div>
  );
}

function OverviewEdit({ form, setForm, save, saving, cancel, canSeePay, isOwner }) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div className="space-y-4" data-testid="overview-edit">
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs text-slate-400 mb-1 block">Name</label><Input value={form.name} onChange={e => set("name", e.target.value)} className="h-9 text-sm" data-testid="ov-name" /></div>
        <div><label className="text-xs text-slate-400 mb-1 block">Title</label><Input value={form.title} onChange={e => set("title", e.target.value)} className="h-9 text-sm" /></div>
        <div><label className="text-xs text-slate-400 mb-1 block">Email</label><Input value={form.email} onChange={e => set("email", e.target.value)} className="h-9 text-sm" /></div>
        <div><label className="text-xs text-slate-400 mb-1 block">Phone</label><Input value={form.phone} onChange={e => set("phone", e.target.value)} className="h-9 text-sm" /></div>
        <div><label className="text-xs text-slate-400 mb-1 block">Start Date</label><Input type="date" value={form.start_date} onChange={e => set("start_date", e.target.value)} className="h-9 text-sm" data-testid="ov-start-date" /></div>
        <div><label className="text-xs text-slate-400 mb-1 block">Birthday</label><Input type="date" value={form.birthday} onChange={e => set("birthday", e.target.value)} className="h-9 text-sm" /></div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">Role</label>
          <Select value={form.role} onValueChange={v => set("role", v)}>
            <SelectTrigger className="h-9 text-sm" data-testid="ov-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["stylist", "colorist", "esthetician", "nail_tech", "educator", "front_desk", "support_staff", "manager"].map(r => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}
              <SelectItem value="owner" disabled={!isOwner}>owner</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">Employment</label>
          <Select value={form.employment_type} onValueChange={v => set("employment_type", v)}>
            <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="full-time">Full-Time</SelectItem>
              <SelectItem value="part-time">Part-Time</SelectItem>
              <SelectItem value="contract">Contract</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {canSeePay && (<>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Pay Type</label>
            <Select value={form.pay_type} onValueChange={v => set("pay_type", v)}>
              <SelectTrigger className="h-9 text-sm" data-testid="ov-pay-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="commission">Commission</SelectItem>
                <SelectItem value="hourly">Hourly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.pay_type === "commission" ? (
            <div><label className="text-xs text-slate-400 mb-1 block">Commission (0.40 = 40%)</label><Input type="number" step="0.01" min="0" max="1" value={form.commission_rate} onChange={e => set("commission_rate", e.target.value)} className="h-9 text-sm" data-testid="ov-commission" /></div>
          ) : (
            <div><label className="text-xs text-slate-400 mb-1 block">Hourly Rate ($/hr)</label><Input type="number" step="0.25" min="0" value={form.hourly_rate} onChange={e => set("hourly_rate", e.target.value)} className="h-9 text-sm" data-testid="ov-hourly" /></div>
          )}
        </>)}
        <div><label className="text-xs text-slate-400 mb-1 block">Emergency Contact</label><Input value={form.emergency_contact} onChange={e => set("emergency_contact", e.target.value)} className="h-9 text-sm" /></div>
        <div><label className="text-xs text-slate-400 mb-1 block">Emergency Phone</label><Input value={form.emergency_phone} onChange={e => set("emergency_phone", e.target.value)} className="h-9 text-sm" /></div>
        <div><label className="text-xs text-slate-400 mb-1 block">Color</label><Input type="color" value={form.color} onChange={e => set("color", e.target.value)} className="h-9 p-1" /></div>
      </div>
      <div><label className="text-xs text-slate-400 mb-1 block">Specializations (comma-separated)</label><Input value={form.specializations} onChange={e => set("specializations", e.target.value)} className="h-9 text-sm" /></div>
      <div><label className="text-xs text-slate-400 mb-1 block">Bio</label><textarea value={form.bio} onChange={e => set("bio", e.target.value)} className="w-full border border-slate-200 rounded-lg p-2 text-sm h-20 resize-none" /></div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={cancel} className="rounded-full">Cancel</Button>
        <Button onClick={save} disabled={saving} className="rounded-full bg-slate-900 text-white hover:bg-slate-800" data-testid="ov-save"><Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  );
}

function ServicesTab({ userId, isManager }) {
  const [quals, setQuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/staff/${userId}/qualifications`); setQuals(r.data || []); }
    catch { toast.error("Couldn't load services"); }
    setLoading(false);
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const remove = async (sid) => {
    try { await api.delete(`/staff/${userId}/qualifications/${sid}`); toast.success("Service removed"); load(); }
    catch { toast.error("Failed"); }
  };

  if (loading) return <p className="text-sm text-slate-400">Loading services…</p>;
  if (!quals.length) return <p className="text-sm text-slate-400" data-testid="services-empty">No services assigned yet.</p>;
  return (
    <div className="space-y-2" data-testid="services-tab">
      {quals.map(q => (
        <div key={q.service_id} className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid={`svc-row-${q.service_id}`}>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{q.service_name || q.name || q.service_id}</p>
            <p className="text-xs text-slate-500">${q.custom_price ?? q.price ?? 0}{q.custom_duration_minutes ? ` · ${q.custom_duration_minutes} min` : ""}</p>
          </div>
          {isManager && (
            <button onClick={() => remove(q.service_id)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50" data-testid={`svc-remove-${q.service_id}`}><Trash2 className="w-4 h-4" /></button>
          )}
        </div>
      ))}
    </div>
  );
}

function DocumentsTab({ userId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("other");
  const [label, setLabel] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/staff/${userId}/documents`); setDocs(r.data || []); }
    catch { toast.error("Couldn't load documents"); }
    setLoading(false);
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("category", category); fd.append("label", label);
      await api.post(`/staff/${userId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setFile(null); setLabel(""); setCategory("other");
      document.getElementById("doc-file-input").value = "";
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/staff/${userId}/documents/${doc.doc_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Couldn't open document"); }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.label}"? This can't be undone.`)) return;
    try { await api.delete(`/staff/${userId}/documents/${doc.doc_id}`); toast.success("Document deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-5" data-testid="documents-tab">
      <div className="bg-[#FAFAFA] border border-slate-200 rounded-2xl p-4">
        <p className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><Upload className="w-4 h-4 text-slate-500" /> Upload a document</p>
        <div className="grid sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">File</label>
            <input id="doc-file-input" type="file" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="doc-file-input"
              className="block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-slate-900 file:text-white hover:file:bg-slate-800 mt-1" />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-9 text-sm w-36" data-testid="doc-category"><SelectValue /></SelectTrigger>
              <SelectContent>{DOC_CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={upload} disabled={uploading || !file} className="rounded-full bg-slate-900 text-white hover:bg-slate-800 h-9" data-testid="doc-upload-btn">
            {uploading ? "Uploading…" : "Upload"}
          </Button>
        </div>
        <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="Label (optional, e.g. 2026 W-9)" className="h-9 text-sm mt-2" data-testid="doc-label" />
      </div>

      {loading ? <p className="text-sm text-slate-400">Loading…</p> : docs.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8" data-testid="documents-empty">No documents uploaded yet.</p>
      ) : (
        <div className="space-y-2">
          {docs.map(d => (
            <div key={d.doc_id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid={`doc-row-${d.doc_id}`}>
              <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0"><FileText className="w-4 h-4 text-slate-500" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{d.label}</p>
                <p className="text-[11px] text-slate-400">
                  <span className="uppercase font-semibold">{DOC_CATEGORIES.find(c => c.id === d.category)?.label || d.category}</span>
                  {" · "}{fmtBytes(d.size)}{" · "}{fmtDate((d.created_at || "").slice(0, 10))}
                </p>
              </div>
              <button onClick={() => download(d)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50" data-testid={`doc-download-${d.doc_id}`} title="Open / download"><Download className="w-4 h-4" /></button>
              <button onClick={() => remove(d)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50" data-testid={`doc-delete-${d.doc_id}`} title="Delete"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PerformanceTab({ userId, stats, canSeePay }) {
  const [tp, setTp] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/training/profile/${userId}`); setTp(r.data); }
      catch { /* training optional */ }
      setLoading(false);
    })();
  }, [userId]);

  return (
    <div className="space-y-6" data-testid="performance-tab">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2 font-['Manrope']">Appointments</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Completed" value={stats.total_completed ?? 0} accent="emerald" />
          <Stat label="No-Shows" value={stats.total_noshows ?? 0} accent="rose" />
          <Stat label="This Month" value={stats.month_appointments ?? 0} accent="indigo" />
          {canSeePay && <Stat label="Month Rev" value={`$${stats.month_revenue ?? 0}`} accent="amber" />}
        </div>
      </div>

      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2 font-['Manrope']">Training</p>
        {loading ? <p className="text-sm text-slate-400">Loading training…</p> : !tp ? (
          <p className="text-sm text-slate-400" data-testid="perf-no-training">No training records.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <Stat label="Avg Score" value={tp.avg_score || 0} accent="indigo" />
              <Stat label="Evaluations" value={tp.total_evaluations || 0} />
              <Stat label="Completed Sessions" value={tp.completed_sessions_count || 0} accent="emerald" />
              <Stat label="Upcoming" value={(tp.upcoming_sessions || []).length} accent="amber" />
            </div>
            {(tp.approvals || []).length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5"><Award className="w-4 h-4 text-emerald-500" /> Approvals</p>
                <div className="flex flex-wrap gap-2">
                  {tp.approvals.map((a, i) => (
                    <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 flex items-center gap-1" data-testid={`perf-approval-${i}`}>
                      <CheckCircle2 className="w-3 h-3" /> {a.track_name || a.track_id || "Approved"}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {(tp.evaluations || []).length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5"><GraduationCap className="w-4 h-4 text-indigo-500" /> Recent evaluations</p>
                {tp.evaluations.slice(0, 8).map((ev, i) => (
                  <div key={ev.eval_id || i} className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-2.5" data-testid={`perf-eval-${i}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{ev.track_name || ev.session_name || "Evaluation"}</p>
                      <p className="text-[11px] text-slate-400">{fmtDate((ev.created_at || "").slice(0, 10))}{ev.evaluator_name ? ` · by ${ev.evaluator_name}` : ""}</p>
                    </div>
                    <span className="text-sm font-semibold tabular-nums text-slate-900 flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" /> {ev.overall_score ?? "—"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 flex items-center gap-1.5"><AlertCircle className="w-4 h-4" /> No evaluations recorded yet.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
